// Canvas 2D renderer (TECH_SPEC §4.1). Logical 540×960 portrait space, scaled
// to the device pixel grid. Flat geometric art: one theme colour + dark ground
// on a light background, no image assets, system fonts only.
//
// Split-lane racing view: forward speed is a pure function of the sim frame
// (TECH_SPEC §4bis.9), so a living ghost sits at exactly the player's world x.
// Overlaid in one lane it is invisible behind the player, so the two runners
// get their own stacked lanes — the opponent on top in a washed-out copy of the
// palette, the player below in full colour — sharing one camera x, one obstacle
// list and one parallax pattern so they read as two tracks of the same world.

import { FQ_ONE, SPEED_RAMP_FRAMES, type Obstacle } from '@beatme/sim';

import {
  FairCam,
  runnerCorpse,
  runnerMassShapes,
  runnerShiftFQ,
  spikeGrooves,
  spikeShapes,
  type CompPose,
  type Pose,
  type Rect as GeomRect,
} from './geometry';

export type { Pose } from './geometry';

export const LOGICAL_W = 540;
export const LOGICAL_H = 960;

/** Pixels per world grid. 540 / 44 ≈ 12 grids visible — enough lead time to
 *  read an obstacle at the 16 grids/s end speed. */
export const GRID_PX = 44;
/** Character sits ~30% from the left in both lanes. */
/** Camera anchor at frame 0. As speed ramps the anchor slides left so the
 *  player sees proportionally further ahead — at 2x speed the old fixed 30%
 *  anchor gave barely half a second of lookahead, which phone playtests
 *  showed is not enough to react to a bar. */
export const PLAYER_SCREEN_X = Math.round(LOGICAL_W * 0.3);
const PLAYER_SCREEN_X_MIN = Math.round(LOGICAL_W * 0.21);
let anchorPx = PLAYER_SCREEN_X;

/**
 * Interpolates the camera anchor with the speed ramp; called once per draw.
 * Fair arm only (§4bis.23): the forward mass shift eats lookahead, so the
 * anchor moves left by the pose comp — `anchorPx − FAIR_CAM_COMP_PX[pose]`,
 * eased by the caller's FairCam. Outside the arm `fairCompPx` is always 0
 * and this line is byte-identical to the shipped one.
 */
export function setCameraFrame(frame: number, fairCompPx = 0): void {
  const k = Math.min(1, Math.max(0, frame / SPEED_RAMP_FRAMES));
  anchorPx = Math.round(PLAYER_SCREEN_X - (PLAYER_SCREEN_X - PLAYER_SCREEN_X_MIN) * k) - fairCompPx;
}

/** A horizontal track: a clipped band with its own ground line. */
export interface Lane {
  /** Top of the band; everything drawn for the lane is clipped to it. */
  top: number;
  /** Ground line inside the band (y of the runner's feet at height 0). */
  groundY: number;
  /** Bottom of the band. */
  bottom: number;
}

/** Below the big distance readout + gap meter (see `drawHud`). */
const LANES_TOP = 190;
/** Headroom must clear a 3-grid bar (132 px) and the head of a runner at the
 *  jump apex (1380 FQ ≈ 59 px of lift + 80 px of runner ≈ 139 px). */
const OPPONENT_HEADROOM = 150;
const OPPONENT_SLAB = 26;
const LANE_GAP = 16;
/** The player's lane is the dominant one: more headroom, ground to the frame. */
const PLAYER_HEADROOM = 168;

export const OPPONENT_LANE: Lane = {
  top: LANES_TOP,
  groundY: LANES_TOP + OPPONENT_HEADROOM,
  bottom: LANES_TOP + OPPONENT_HEADROOM + OPPONENT_SLAB,
};

const DIVIDER_Y = OPPONENT_LANE.bottom + LANE_GAP / 2;

export const PLAYER_LANE: Lane = {
  top: OPPONENT_LANE.bottom + LANE_GAP,
  groundY: OPPONENT_LANE.bottom + LANE_GAP + PLAYER_HEADROOM,
  bottom: LOGICAL_H,
};

/** How far the opponent lane is blended toward the background: alive vs. dead. */
export const GHOST_WASH = 0.5;
export const GHOST_WASH_DEAD = 0.76;
/** Peak arm only (§4bis.22): once overtaken, the living opponent lane washes
 *  further — visibly beaten, still legible. A dead ghost still wins at 0.76. */
export const GHOST_WASH_DEFEATED = 0.68;
/** The overtaken wash eases in over this many render frames. */
export const DEFEAT_WASH_FRAMES = 30;

const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", system-ui, sans-serif';

export interface Palette {
  bg: string;
  bgAlt: string;
  ground: string;
  groundEdge: string;
  accent: string;
  accentSoft: string;
  ink: string;
  inkSoft: string;
  good: string;
  bad: string;
}

export const PALETTES: Record<string, Palette> = {
  default: {
    bg: '#f2efe9',
    bgAlt: '#e7e2d8',
    ground: '#21262c',
    groundEdge: '#2f3640',
    accent: '#ff5a3d',
    accentSoft: '#ffb3a5',
    ink: '#1b1f24',
    inkSoft: '#7d8189',
    good: '#1e9e5a',
    bad: '#e0402f',
  },
  // Weekly themes must read as a *different place* at a glance (design §7.2/§11)
  // — the first palette set only shifted the bg hue a few points and desert vs
  // volcano vs default were indistinguishable in the theme sweep shots.
  volcano: {
    bg: '#ffd9c2',
    bgAlt: '#f7bd9d',
    ground: '#38120a',
    groundEdge: '#571d0e',
    accent: '#e42f0a',
    accentSoft: '#ff9770',
    ink: '#38120a',
    inkSoft: '#96604a',
    good: '#0c7a3f',
    bad: '#b81f04',
  },
  deepsea: {
    bg: '#cfe6f2',
    bgAlt: '#aed2e6',
    ground: '#081722',
    groundEdge: '#12293a',
    accent: '#0a6f95',
    accentSoft: '#6fb8d4',
    ink: '#081722',
    inkSoft: '#527388',
    good: '#0c7a55',
    bad: '#c93b1e',
  },
  forest: {
    bg: '#d9ecc4',
    bgAlt: '#c0dda3',
    ground: '#12210f',
    groundEdge: '#213c1c',
    accent: '#2f7d24',
    accentSoft: '#8cc47c',
    ink: '#12210f',
    inkSoft: '#5b7355',
    good: '#1f6e33',
    bad: '#bb3a17',
  },
  desert: {
    bg: '#f5dea3',
    bgAlt: '#e8ca7e',
    ground: '#2e2008',
    groundEdge: '#4c3810',
    accent: '#8a5203',
    accentSoft: '#d9a94e',
    ink: '#2e2008',
    inkSoft: '#7c6836',
    good: '#2c6e22',
    bad: '#b13413',
  },
};

export function paletteFor(theme: string | undefined): Palette {
  const key = (theme ?? '').toLowerCase();
  return PALETTES[key] ?? PALETTES['default']!;
}

/** Blend every mark toward the background — the opponent lane is the same world
 *  seen through gauze. `bg` itself is left alone so gap punch-outs and label
 *  chips still match the page exactly. */
export function washPalette(p: Palette, amount: number): Palette {
  const w = (c: string): string => mixHex(c, p.bg, amount);
  return {
    bg: p.bg,
    // Half-washed: the horizon band is already a whisper against `bg`, and
    // erasing it would cost the lane its parallax and the shared-world read.
    bgAlt: mixHex(p.bgAlt, p.bg, amount * 0.5),
    ground: w(p.ground),
    groundEdge: w(p.groundEdge),
    accent: w(p.accent),
    accentSoft: w(p.accentSoft),
    ink: w(p.ink),
    inkSoft: w(p.inkSoft),
    good: w(p.good),
    bad: w(p.bad),
  };
}

export interface ActorSnapshot {
  /** FQ world coordinates. */
  x: number;
  y: number;
  sliding: boolean;
  grounded: boolean;
  dead: boolean;
}

export interface FloatingText {
  text: string;
  /** Logical screen coordinates at spawn. */
  sx: number;
  sy: number;
  /** Frames left / total, for fade + rise. */
  life: number;
  maxLife: number;
  color: string;
  size: number;
}

export interface Scene {
  /** Player sim frame — drives the speed-aware camera anchor. */
  frame: number;
  obstacles: readonly Obstacle[];
  playerPrev: ActorSnapshot;
  player: ActorSnapshot;
  ghostPrev: ActorSnapshot | null;
  ghost: ActorSnapshot | null;
  /** Ghost has died — its lane dims and its runner holds the death pose. */
  ghostDead: boolean;
  /** World x of the opponent's death flag, if they died. */
  ghostDeathX: number | null;
  /** World x positions where the ghost used a revive (design §3.1). */
  ghostReviveMarks: readonly number[];
  /** Lane labels: the opponent's nickname, and 你 / You. */
  ghostName: string;
  playerName: string;
  ghostReviveLabel: string;
  distanceM: number;
  /** playerM − opponentM: positive = you lead (green). null = no HUD gap. */
  gapM: number | null;
  /**
   * Peak arm only (§4bis.22): true from the overtake beat on. Drives the
   * defeated lane wash; false (every run outside the arm) renders exactly the
   * shipped frame — the PEAK_PCT=0 baselines depend on it.
   */
  overtaken: boolean;
  /** Peak arm only: the localized 「领先 +{d} 米」 line that replaces the ±gap
   *  readout once `overtaken`. Null = draw the shipped gap meter. */
  leadText: string | null;
  /**
   * Fair arm only (§4bis.23): honest silhouettes + centered runner anchor +
   * pose camera comp. False (every run outside the arm) renders exactly the
   * shipped frame — the FAIR_PCT=0 baselines depend on it.
   */
  fair: boolean;
  /** Pose AT death ('stand' folds jump in), for the dead camera comp — the
   *  death photo settles instantly on it (§4bis.23). Null while alive. */
  fairDeathPose: 'stand' | 'slide' | null;
  hudVisible: boolean;
  metersLabel: string;
  floating: readonly FloatingText[];
  /** 0..1 white flash (overtake beat, design §2.5). */
  flash: number;
  slowMo: boolean;
  /**
   * §4bis.30 — the 「活过了」 seal, fired MID-RUN on the frame the duel's line is
   * passed. Drawn on the CANVAS, never as a DOM overlay: the run is still going,
   * and a DOM node over the play area could swallow the tap that clears the
   * obstacle the player is about to hit. `elapsedMs` is wall clock (the loop is
   * in slow-mo, so frames are the wrong ruler for a 1.4 s beat) and drives
   * punch-in → hold → fade. Null on every non-duel run, and at `DUEL_PCT = 0`.
   */
  duelSeal: { text: string; elapsedMs: number; plant?: string } | null;
  /**
   * §4bis.30 — the sender's grave, ticked in the PLAYER's own lane at
   * `sourceEndM` and scrolling away behind them. The opponent lane already
   * carries their death flag; this is the same place, marked on the track the
   * player is still running, which is what makes "past it" legible.
   */
  duelMark: { x: number; label: string } | null;
}

/** Everything a lane needs to draw its one runner. */
interface LaneActor {
  sx: number;
  /** FQ height above the lane's ground line. */
  y: number;
  pose: Pose;
  /** Drives the stride phase. */
  worldX: number;
  deathX: number | null;
  reviveMarks: readonly number[];
  /** Waypoints (death flag, revive pips) keep the undimmed lane palette: they
   *  are what the player is chasing, so post-death dimming must not eat them. */
  markPalette: Palette;
  label: string;
  /** §4bis.30: the duel grave tick, on the player's lane only. */
  duelMark?: { x: number; label: string } | null;
}

export class Renderer {
  private readonly ctx: CanvasRenderingContext2D;
  private palette: Palette = paletteFor(undefined);
  private ghostPalette: Palette = washPalette(this.palette, GHOST_WASH);
  private ghostPaletteDead: Palette = washPalette(this.palette, GHOST_WASH_DEAD);
  /** Overtaken-wash progress in render frames, 0..DEFEAT_WASH_FRAMES (§4bis.22).
   *  Advances only while a scene says `overtaken`; snaps back to 0 otherwise,
   *  so a fresh run always starts from the shipped wash. */
  private defeatFrames = 0;
  private ghostPaletteDefeated: Palette = washPalette(this.palette, GHOST_WASH_DEFEATED);
  /** Fair-arm camera comp easing (§4bis.23); untouched while `scene.fair` is
   *  false, so dormant runs never read it. */
  private readonly fairCam = new FairCam();
  private scale = 1;
  private cssW = LOGICAL_W;
  private cssH = LOGICAL_H;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('canvas 2d context unavailable');
    this.ctx = ctx;
    this.resize();
  }

  setTheme(theme: string | undefined): void {
    this.palette = paletteFor(theme);
    this.ghostPalette = washPalette(this.palette, GHOST_WASH);
    this.ghostPaletteDead = washPalette(this.palette, GHOST_WASH_DEAD);
    this.ghostPaletteDefeated = washPalette(this.palette, GHOST_WASH_DEFEATED);
  }

  /** The living opponent lane's palette for this frame. Outside the peak arm
   *  `overtaken` is always false, `defeatFrames` stays 0 and this returns the
   *  shipped `ghostPalette` — bit-identical rendering at PEAK_PCT=0. */
  private ghostLanePalette(overtaken: boolean): Palette {
    if (!overtaken) {
      this.defeatFrames = 0;
      return this.ghostPalette;
    }
    if (this.defeatFrames >= DEFEAT_WASH_FRAMES) return this.ghostPaletteDefeated;
    this.defeatFrames++;
    const t = this.defeatFrames / DEFEAT_WASH_FRAMES;
    const eased = 1 - (1 - t) ** 3; // cubic ease-out
    return washPalette(this.palette, GHOST_WASH + (GHOST_WASH_DEFEATED - GHOST_WASH) * eased);
  }

  getPalette(): Palette {
    return this.palette;
  }

  /** Fresh run → steady-state stand comp (no 5 px transient on frame 0). */
  resetFairCam(): void {
    this.fairCam.reset();
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const rect = this.canvas.getBoundingClientRect();
    this.cssW = rect.width || LOGICAL_W;
    this.cssH = rect.height || LOGICAL_H;
    const w = Math.max(1, Math.round(this.cssW * dpr));
    const h = Math.max(1, Math.round(this.cssH * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.scale = w / LOGICAL_W;
  }

  draw(scene: Scene, alpha: number): void {
    const ctx = this.ctx;
    const p = this.palette;
    ctx.setTransform(this.scale, 0, 0, this.scale, 0, 0);

    const px = lerp(scene.playerPrev.x, scene.player.x, alpha);
    const py = lerp(scene.playerPrev.y, scene.player.y, alpha);
    // One camera for both lanes: the same track, side by side.
    const camX = px;
    const playerPose = poseOf(scene.player);
    // Fair arm only (§4bis.23): pose-dependent lookahead comp, eased 6 frames
    // on a pose change, settled INSTANTLY in the death photo. Outside the arm
    // comp is 0 and setCameraFrame is byte-identical to the shipped call.
    let comp = 0;
    if (scene.fair) {
      const target: CompPose =
        playerPose === 'dead'
          ? (scene.fairDeathPose ?? 'stand')
          : playerPose === 'run'
            ? 'stand'
            : playerPose;
      comp = this.fairCam.step(target, scene.player.dead);
    }
    setCameraFrame(scene.frame, comp);

    ctx.fillStyle = p.bg;
    ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H);

    // Fair arm: sprite mass is centered on the hitbox, so the translate point
    // moves forward by hitW/2 (both lanes — the ghost uses the same body).
    const shiftPx = (pose: Pose): number =>
      (runnerShiftFQ(pose, scene.fair) / FQ_ONE) * GRID_PX;

    const gx = scene.ghost ? lerp(scene.ghostPrev?.x ?? scene.ghost.x, scene.ghost.x, alpha) : 0;
    const gy = scene.ghost ? lerp(scene.ghostPrev?.y ?? scene.ghost.y, scene.ghost.y, alpha) : 0;
    const ghostPose = scene.ghost ? poseOf(scene.ghost) : 'run';
    this.drawLane(OPPONENT_LANE, scene.ghostDead ? this.ghostPaletteDead : this.ghostLanePalette(scene.overtaken), scene, camX, {
      sx: worldToScreenX(gx, camX) + shiftPx(ghostPose),
      y: gy,
      pose: ghostPose,
      worldX: gx,
      deathX: scene.ghostDeathX,
      reviveMarks: scene.ghostReviveMarks,
      markPalette: this.ghostPalette,
      label: scene.ghostName,
    });

    this.drawDivider();

    this.drawLane(PLAYER_LANE, p, scene, camX, {
      sx: worldToScreenX(px, camX) + shiftPx(playerPose),
      y: py,
      pose: playerPose,
      worldX: px,
      deathX: null,
      reviveMarks: EMPTY_MARKS,
      markPalette: p,
      label: scene.playerName,
      duelMark: scene.duelMark,
    });

    if (scene.slowMo) this.drawSlowMoStreaks();
    this.drawFloating(scene.floating);
    if (scene.duelSeal) {
      this.drawDuelSeal(scene.duelSeal.text, scene.duelSeal.elapsedMs, scene.duelSeal.plant);
    }
    if (scene.hudVisible) this.drawHud(scene);

    if (scene.flash > 0) {
      ctx.fillStyle = `rgba(255,255,255,${clamp01(scene.flash) * 0.85})`;
      ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H);
    }
  }

  // --- lanes --------------------------------------------------------------

  private drawLane(
    lane: Lane,
    p: Palette,
    scene: Scene,
    camX: number,
    actor: LaneActor,
  ): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, lane.top, LOGICAL_W, lane.bottom - lane.top);
    ctx.clip();

    this.drawBackground(lane, p, camX);
    this.drawGround(lane, p, scene.obstacles, camX);
    this.drawObstacles(lane, p, scene.obstacles, camX, scene.fair);

    if (actor.deathX !== null) this.drawDeathFlag(lane, actor.markPalette, actor.deathX, camX);
    if (actor.duelMark) {
      this.drawDuelMark(lane, actor.markPalette, actor.duelMark.x, camX, actor.duelMark.label);
    }
    for (const mx of actor.reviveMarks) {
      this.drawReviveMark(lane, actor.markPalette, mx, camX, scene.ghostReviveLabel);
    }

    this.drawRunner(lane, p, actor.sx, actor.y, actor.pose, actor.worldX, scene.fair);
    this.drawLaneLabel(lane, p, actor.label);
    ctx.restore();
  }

  /** A hairline between the tracks — enough to separate, not to divide. */
  private drawDivider(): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = 0.24;
    ctx.fillStyle = this.palette.inkSoft;
    for (let x = 0; x < LOGICAL_W; x += 24) ctx.fillRect(x, DIVIDER_Y, 12, 1);
    ctx.restore();
  }

  private drawLaneLabel(lane: Lane, p: Palette, text: string): void {
    if (!text) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.font = `700 13px ${FONT_STACK}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const label = fitText(ctx, text, 150);
    const w = ctx.measureText(label).width + 20;
    const y = lane.top + 6;
    ctx.globalAlpha = 0.82;
    ctx.fillStyle = p.bg;
    ctx.beginPath();
    roundRect(ctx, 12, y, w, 22, 11);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.fillStyle = p.ink;
    ctx.fillText(label, 22, y + 12);
    ctx.restore();
  }

  // --- world layers -------------------------------------------------------

  private drawBackground(lane: Lane, p: Palette, camX: number): void {
    const ctx = this.ctx;
    const band = Math.min(190, lane.groundY - lane.top);
    const bandTop = lane.groundY - band;

    // Far band + horizon block, parallax 0.15×. Both lanes use the same camX
    // and the same factor, so the skylines line up vertically.
    ctx.fillStyle = p.bgAlt;
    ctx.fillRect(0, bandTop, LOGICAL_W, band);

    const far = -((camX / FQ_ONE) * GRID_PX * 0.15);
    ctx.fillStyle = p.bg;
    const blockW = 96;
    const gapW = 150;
    const period = blockW + gapW;
    let start = far % period;
    if (start > 0) start -= period;
    for (let x = start; x < LOGICAL_W; x += period) {
      const h = 64 + ((Math.floor((x - far) / period) * 37) % 46);
      ctx.fillRect(x, bandTop, blockW, Math.max(0, band - h));
    }

    // Near stripes on the ground band, parallax 0.45×.
    const near = -((camX / FQ_ONE) * GRID_PX * 0.45);
    ctx.fillStyle = p.accentSoft;
    ctx.globalAlpha = 0.35;
    const sPeriod = 220;
    let s = near % sPeriod;
    if (s > 0) s -= sPeriod;
    for (let x = s; x < LOGICAL_W; x += sPeriod) {
      ctx.fillRect(x, lane.groundY - 26, 54, 6);
    }
    ctx.globalAlpha = 1;
  }

  private drawGround(
    lane: Lane,
    p: Palette,
    obstacles: readonly Obstacle[],
    camX: number,
  ): void {
    const ctx = this.ctx;
    const slab = lane.bottom - lane.groundY;
    ctx.fillStyle = p.ground;
    ctx.fillRect(0, lane.groundY, LOGICAL_W, slab);
    ctx.fillStyle = p.groundEdge;
    ctx.fillRect(0, lane.groundY, LOGICAL_W, 5);

    // Tall slabs (player lane) get faint strata so the lower 40% of the frame
    // isn't a featureless void; they scroll at half speed for cheap depth.
    if (slab > 120) {
      ctx.save();
      ctx.globalAlpha *= 0.28;
      ctx.fillStyle = p.groundEdge;
      const shift = (camX / FQ_ONE) * GRID_PX * 0.5;
      for (let y = lane.groundY + 46; y < lane.bottom - 20; y += 58) {
        const phase = ((y * 7919 - shift) % (LOGICAL_W + 220) + (LOGICAL_W + 220)) % (LOGICAL_W + 220);
        ctx.fillRect(phase - 220, y, 130 + (y % 3) * 30, 4);
      }
      ctx.restore();
    }

    // Punch the gaps back out to background colour.
    ctx.fillStyle = p.bg;
    for (const o of visible(obstacles, camX)) {
      if (o.type !== 'gap') continue;
      const x0 = worldToScreenX(o.x, camX);
      const x1 = worldToScreenX(o.x + o.w, camX);
      ctx.fillRect(x0, lane.groundY, x1 - x0, slab);
      ctx.fillStyle = p.groundEdge;
      ctx.fillRect(x0 - 4, lane.groundY, 4, Math.min(22, slab));
      ctx.fillRect(x1, lane.groundY, 4, Math.min(22, slab));
      ctx.fillStyle = p.bg;
    }
  }

  private drawObstacles(
    lane: Lane,
    p: Palette,
    obstacles: readonly Obstacle[],
    camX: number,
    fair: boolean,
  ): void {
    const ctx = this.ctx;
    const groundY = lane.groundY;
    for (const o of visible(obstacles, camX)) {
      const x0 = worldToScreenX(o.x, camX);
      const w = (o.w / FQ_ONE) * GRID_PX;
      if (o.type === 'spike') {
        if (fair) {
          this.drawFairSpike(lane, p, o, camX);
          continue;
        }
        const h = GRID_PX; // 1 grid tall
        ctx.fillStyle = p.accent;
        ctx.beginPath();
        ctx.moveTo(x0, groundY);
        ctx.lineTo(x0 + w / 2, groundY - h);
        ctx.lineTo(x0 + w, groundY);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = p.ground;
        ctx.fillRect(x0 - 2, groundY - 3, w + 4, 5);
      } else if (o.type === 'bar') {
        const top = groundY - (3072 / FQ_ONE) * GRID_PX;
        const bottom = groundY - (1126 / FQ_ONE) * GRID_PX;
        const cx = x0 + w / 2;
        ctx.fillStyle = p.ground;
        ctx.fillRect(cx - 3, bottom, 6, groundY - bottom); // post
        ctx.fillStyle = p.accent;
        ctx.fillRect(x0 - 14, bottom - 12, w + 28, 12); // beam
        ctx.fillStyle = p.ground;
        ctx.fillRect(x0 - 14, top, w + 28, 4); // upper rail
        ctx.globalAlpha = 0.25;
        ctx.fillStyle = p.accent;
        ctx.fillRect(x0 - 14, top + 4, w + 28, bottom - 12 - top - 4);
        ctx.globalAlpha = 1;
      }
    }
  }

  /** FQ world rect (y up from the lane ground) → screen, for geometry shapes. */
  private geomRect(
    lane: Lane,
    r: GeomRect,
    camX: number,
  ): { x: number; y: number; w: number; h: number } {
    const x = worldToScreenX(r.x0, camX);
    const y = lane.groundY - ((r.y1 / FQ_ONE) * GRID_PX);
    return {
      x,
      y,
      w: ((r.x1 - r.x0) / FQ_ONE) * GRID_PX,
      h: ((r.y1 - r.y0) / FQ_ONE) * GRID_PX,
    };
  }

  /**
   * §4bis.23 honest spike: the kill rect exactly, crowned with three teeth on
   * the kill line (silhouette ⊇ kill box — geometry.ts is the single source),
   * two darkened v-groove columns as texture inside the body, and the shipped
   * ground lip unchanged.
   */
  private drawFairSpike(lane: Lane, p: Palette, o: Obstacle, camX: number): void {
    const ctx = this.ctx;
    const groundY = lane.groundY;
    ctx.fillStyle = p.accent;
    for (const s of spikeShapes(o, true)) {
      if (s.kind === 'rect') {
        const r = this.geomRect(lane, s.r, camX);
        ctx.fillRect(r.x, r.y, r.w, r.h);
      } else if (s.kind === 'tri') {
        ctx.beginPath();
        ctx.moveTo(worldToScreenX(s.a[0], camX), groundY - (s.a[1] / FQ_ONE) * GRID_PX);
        ctx.lineTo(worldToScreenX(s.b[0], camX), groundY - (s.b[1] / FQ_ONE) * GRID_PX);
        ctx.lineTo(worldToScreenX(s.c[0], camX), groundY - (s.c[1] / FQ_ONE) * GRID_PX);
        ctx.closePath();
        ctx.fill();
      }
    }
    // Grooves: accent darkened 30% — texture INSIDE the body, never holes.
    ctx.fillStyle = mixHex(p.accent, '#000000', 0.3);
    for (const g of spikeGrooves(o)) {
      const r = this.geomRect(lane, g, camX);
      ctx.fillRect(r.x, r.y, r.w, r.h);
    }
    const x0 = worldToScreenX(o.x, camX);
    const w = (o.w / FQ_ONE) * GRID_PX;
    ctx.fillStyle = p.ground;
    ctx.fillRect(x0 - 2, groundY - 3, w + 4, 5);
  }

  /**
   * §4bis.23 death-frame flash over the frozen death photo (collision deaths
   * only; session drives the timeline). 0–120 ms outlines fade in; 120–500 ms
   * the overlap fill eases 0.5→0.35; at 500 ms it settles — outlines at 0.35
   * with the copy chip for as long as the photo shows.
   */
  drawDeathFlash(f: {
    playerBox: GeomRect;
    obstacleBox: GeomRect;
    camX: number;
    elapsedMs: number;
    chip: string;
  }): void {
    const ctx = this.ctx;
    const p = this.palette;
    const lane = PLAYER_LANE;
    const t = f.elapsedMs;
    const player = this.geomRect(lane, f.playerBox, f.camX);
    const obstacle = this.geomRect(lane, f.obstacleBox, f.camX);

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, lane.top, LOGICAL_W, lane.bottom - lane.top);
    ctx.clip();

    // Overlap fill first, outlines over it.
    const ix = Math.max(player.x, obstacle.x);
    const iy = Math.max(player.y, obstacle.y);
    const ix1 = Math.min(player.x + player.w, obstacle.x + obstacle.w);
    const iy1 = Math.min(player.y + player.h, obstacle.y + obstacle.h);
    if (ix1 > ix && iy1 > iy && t >= 120) {
      const k = Math.min(1, (t - 120) / 380);
      ctx.globalAlpha = 0.5 + (0.35 - 0.5) * k;
      ctx.fillStyle = p.bad;
      ctx.fillRect(ix, iy, ix1 - ix, iy1 - iy);
    }

    ctx.globalAlpha = t < 120 ? t / 120 : t < 500 ? 1 : 0.35;
    ctx.strokeStyle = p.bad;
    ctx.lineWidth = 2;
    ctx.strokeRect(player.x, player.y, player.w, player.h);
    ctx.strokeRect(obstacle.x, obstacle.y, obstacle.w, obstacle.h);
    ctx.globalAlpha = 1;

    if (t >= 500) {
      // The readable half: the chip in the floating-text style, below the
      // boxes (the player-lane ground slab is deep enough to own it).
      ctx.font = `800 15px ${FONT_STACK}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const cx = Math.max(90, Math.min(LOGICAL_W - 90, (ix + ix1) / 2 || player.x + player.w / 2));
      const cy = lane.groundY + 36;
      const w = ctx.measureText(f.chip).width;
      const padX = 14;
      const h = 15 + 18;
      ctx.fillStyle = p.bg;
      ctx.beginPath();
      roundRect(ctx, cx - w / 2 - padX, cy - h / 2, w + padX * 2, h, h / 2);
      ctx.fill();
      ctx.fillStyle = p.bad;
      ctx.fillRect(cx - w / 2, cy + h / 2 - 4, w, 3);
      ctx.fillStyle = p.ink;
      ctx.fillText(f.chip, cx, cy);
    }
    ctx.restore();
  }

  private drawDeathFlag(lane: Lane, p: Palette, worldX: number, camX: number): void {
    const ctx = this.ctx;
    const x = worldToScreenX(worldX, camX);
    if (x < -40 || x > LOGICAL_W + 40) return;
    const groundY = lane.groundY;
    ctx.fillStyle = p.ink;
    ctx.fillRect(x - 1.5, groundY - 74, 3, 74);
    ctx.fillStyle = p.accent;
    ctx.beginPath();
    ctx.moveTo(x + 1.5, groundY - 74);
    ctx.lineTo(x + 34, groundY - 64);
    ctx.lineTo(x + 1.5, groundY - 54);
    ctx.closePath();
    ctx.fill();
  }

  /**
   * §4bis.30: the sender's grave, ticked on the player's own lane. A dashed
   * post rather than the opponent's solid death flag — this is not a runner's
   * end, it is a line on the ground the player has already crossed or is about
   * to.
   *
   * The label sits BELOW the ground line, on the slab. Measured, not guessed:
   * the first capture put it above the post at `groundY - 108`, where it
   * collided head-on with the overtake chip — and those two beats fire on the
   * SAME frame by construction (the chip fires when you pass their score, which
   * is this exact x). The slab is the one large empty surface in the player
   * lane, and a marker written into the ground reads as part of the track.
   */
  private drawDuelMark(
    lane: Lane,
    p: Palette,
    worldX: number,
    camX: number,
    label: string,
  ): void {
    const ctx = this.ctx;
    const x = worldToScreenX(worldX, camX);
    if (x < -140 || x > LOGICAL_W + 140) return;
    ctx.save();
    // The post stops short of the runner's head so it never fights the figure.
    ctx.fillStyle = p.inkSoft;
    ctx.globalAlpha = 0.5;
    for (let y = lane.groundY - 56; y < lane.groundY; y += 12) {
      ctx.fillRect(x - 1.5, y, 3, 7);
    }
    ctx.globalAlpha = 0.85;
    ctx.font = `700 15px ${FONT_STACK}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const text = fitText(ctx, label, 240);
    const w = ctx.measureText(text).width + 20;
    ctx.fillStyle = p.groundEdge;
    ctx.beginPath();
    roundRect(ctx, x - w / 2, lane.groundY + 14, w, 24, 12);
    ctx.fill();
    ctx.fillStyle = p.bg;
    ctx.fillText(text, x, lane.groundY + 27);
    ctx.restore();
  }

  /**
   * §4bis.30: the 「活过了」 seal, stamped on the canvas over the OPPONENT lane.
   *
   * That placement is the whole safety argument. The run is still live, so the
   * stamp must not sit anywhere the player needs to read — and by construction
   * the opponent is already dead when this fires (the line is their grave plus
   * one second), so their lane is the one surface with nothing left to say.
   * The player's own lane, where the next obstacle is, stays completely clear.
   *
   * Wall-clock timed because the loop is in slow-mo when this fires: punch-in
   * 180 ms (scale 1.18 → 1.0), hold 900 ms, fade 320 ms.
   *
   * §4bis.31 adds one optional line UNDER the box: the hand-back plant. It
   * shares the stamp's alpha and scale, sits inside the same untappable canvas
   * layer, costs zero frames and zero interaction, and is absent entirely
   * outside the handoff arm — which is what keeps `HANDOFF_PCT = 0` pixel
   * identical to the shipped build.
   */
  private drawDuelSeal(text: string, elapsedMs: number, plant?: string): void {
    const ctx = this.ctx;
    const p = this.palette;
    const punch = 180;
    const hold = 900;
    const fade = 320;
    if (elapsedMs < 0 || elapsedMs > punch + hold + fade) return;
    const scale = elapsedMs < punch ? 1.18 - 0.18 * (elapsedMs / punch) : 1;
    const alpha = elapsedMs > punch + hold ? 1 - (elapsedMs - punch - hold) / fade : 1;
    const cy = (OPPONENT_LANE.top + OPPONENT_LANE.bottom) / 2;

    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
    ctx.translate(LOGICAL_W / 2, cy);
    ctx.scale(scale, scale);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `800 56px ${FONT_STACK}`;
    const w = ctx.measureText(text).width;
    const boxW = w + 56;
    const boxH = 88;
    ctx.fillStyle = p.bg;
    ctx.beginPath();
    roundRect(ctx, -boxW / 2, -boxH / 2, boxW, boxH, 16);
    ctx.fill();
    ctx.fillStyle = p.accent;
    ctx.fillRect(-boxW / 2, boxH / 2 - 6, boxW, 6);
    ctx.fillStyle = p.ink;
    ctx.fillText(text, 0, 0);
    if (plant) {
      // Deliberately small and quiet: this is a SEED, not an ask. The ask
      // belongs at the death frame, where the margin is finally non-zero.
      ctx.font = `600 22px ${FONT_STACK}`;
      ctx.fillStyle = p.ink;
      ctx.globalAlpha = Math.max(0, Math.min(1, alpha)) * 0.72;
      ctx.fillText(plant, 0, boxH / 2 + 24);
    }
    ctx.restore();
  }

  private drawReviveMark(
    lane: Lane,
    p: Palette,
    worldX: number,
    camX: number,
    label: string,
  ): void {
    const ctx = this.ctx;
    const x = worldToScreenX(worldX, camX);
    if (x < -40 || x > LOGICAL_W + 40) return;
    ctx.globalAlpha = 0.7;
    ctx.fillStyle = p.inkSoft;
    ctx.beginPath();
    ctx.arc(x, lane.groundY - 96, 13, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = p.bg;
    ctx.font = `600 14px ${FONT_STACK}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, x, lane.groundY - 95);
    ctx.globalAlpha = 1;
  }

  // --- character ----------------------------------------------------------

  /** footYFQ is height above the lane's ground line; worldX drives the stride. */
  private drawRunner(
    lane: Lane,
    p: Palette,
    sx: number,
    footYFQ: number,
    pose: Pose,
    worldX: number,
    fair: boolean,
  ): void {
    // §4bis.23: in the fair arm the in-flight body is the honest capsule from
    // geometry.ts (mass ⊇ hitbox), and the corpse is that SAME capsule fallen
    // (`runnerCorpse`) — one character alive and dead. Flag-off keeps the
    // shipped stick figure, face-plant included, byte-identical.
    if (fair) {
      this.drawFairRunner(lane, p, sx, footYFQ, pose, worldX);
      return;
    }
    const ctx = this.ctx;
    const u = GRID_PX;
    const footY = lane.groundY - (footYFQ / FQ_ONE) * u;
    const phase = ((worldX / FQ_ONE) * 1.9) % 1;
    const swing = Math.sin(phase * Math.PI * 2);

    ctx.save();
    ctx.translate(sx, footY);
    ctx.lineCap = 'round';
    ctx.strokeStyle = p.ink;
    ctx.fillStyle = p.ink;

    if (pose === 'slide') {
      // 0.8 × 0.9 grid hitbox: a forward-diving crouch.
      const h = 0.9 * u;
      const w = 0.9 * u;
      ctx.beginPath();
      roundRect(ctx, -w * 0.35, -h, w, h * 0.72, h * 0.3);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(w * 0.42, -h * 0.62, 0.24 * u, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 0.11 * u;
      ctx.beginPath();
      ctx.moveTo(-w * 0.3, -h * 0.28);
      ctx.lineTo(-w * 0.62, -0.02 * u);
      ctx.stroke();
      // speed dust
      ctx.globalAlpha *= 0.5;
      ctx.fillStyle = p.accent;
      ctx.fillRect(-w * 1.5, -0.1 * u, 0.5 * u, 3);
      ctx.fillRect(-w * 1.15, -0.28 * u, 0.35 * u, 3);
      ctx.restore();
      return;
    }

    const bob = pose === 'run' ? Math.abs(swing) * 2.2 : 0;
    const hipY = -0.62 * u - bob;
    const shoulderY = -1.28 * u - bob;
    const headY = -1.56 * u - bob;
    const headR = 0.26 * u;
    // dead = face-plant: the whole figure rotated flat onto the ground.
    const lean = pose === 'jump' ? -0.06 : pose === 'dead' ? Math.PI / 2 - 0.14 : 0;

    ctx.save();
    ctx.rotate(lean);

    // torso
    ctx.beginPath();
    roundRect(ctx, -0.17 * u, shoulderY, 0.36 * u, hipY - shoulderY + 0.05 * u, 0.16 * u);
    ctx.fill();

    // head
    ctx.beginPath();
    ctx.arc(0.05 * u, headY, headR, 0, Math.PI * 2);
    ctx.fill();

    ctx.lineWidth = 0.13 * u;
    if (pose === 'jump') {
      // tuck: knees up front, trailing leg back, arms swept up
      ctx.beginPath();
      ctx.moveTo(0, hipY);
      ctx.lineTo(0.34 * u, hipY + 0.12 * u);
      ctx.lineTo(0.24 * u, hipY + 0.5 * u);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, hipY);
      ctx.lineTo(-0.3 * u, hipY + 0.3 * u);
      ctx.lineTo(-0.44 * u, hipY + 0.05 * u);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, shoulderY + 0.08 * u);
      ctx.lineTo(0.3 * u, shoulderY - 0.24 * u);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, shoulderY + 0.08 * u);
      ctx.lineTo(-0.3 * u, shoulderY - 0.12 * u);
      ctx.stroke();
    } else if (pose === 'dead') {
      // Slack limbs; the body is already rotated flat by `lean`. The frozen
      // frame is the death photo — it must read as "down", not a T-pose.
      ctx.beginPath();
      ctx.moveTo(0, hipY);
      ctx.lineTo(0.3 * u, hipY + 0.42 * u);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, hipY);
      ctx.lineTo(-0.18 * u, hipY + 0.5 * u);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, shoulderY + 0.1 * u);
      ctx.lineTo(0.5 * u, shoulderY + 0.28 * u);
      ctx.stroke();
    } else {
      const legSwing = swing * 0.42 * u;
      ctx.beginPath();
      ctx.moveTo(0, hipY);
      ctx.lineTo(legSwing * 0.6, hipY + 0.34 * u);
      ctx.lineTo(legSwing, 0);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, hipY);
      ctx.lineTo(-legSwing * 0.6, hipY + 0.34 * u);
      ctx.lineTo(-legSwing, 0);
      ctx.stroke();
      const armSwing = -swing * 0.3 * u;
      ctx.beginPath();
      ctx.moveTo(0, shoulderY + 0.1 * u);
      ctx.lineTo(armSwing, shoulderY + 0.5 * u);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, shoulderY + 0.1 * u);
      ctx.lineTo(-armSwing, shoulderY + 0.5 * u);
      ctx.stroke();
    }

    ctx.restore();
    if (pose === 'dead') {
      // impact dust, unrotated
      ctx.globalAlpha *= 0.5;
      ctx.fillStyle = p.inkSoft;
      ctx.fillRect(-0.95 * u, -0.12 * u, 0.34 * u, 4);
      ctx.fillRect(-0.55 * u, -0.3 * u, 0.24 * u, 4);
    }
    ctx.restore();
  }

  /**
   * §4bis.23 fair-arm body: the mass capsule straight from geometry.ts (the
   * single source the probe measures), face dot, and thin limbs that may
   * overshoot the capsule (fair-safe: overshoot makes survivals look lucky,
   * never deaths look clear). Translate origin = hitbox center at foot level
   * (the caller already applied the +hitW/2 shift to `sx`).
   *
   * The corpse comes out of the same path (H01): `runnerCorpse()` is this very
   * capsule, fallen — same rect, same radius, same ink/bg tokens, same 0.12 u
   * line weight — so alive and dead are one character.
   */
  private drawFairRunner(
    lane: Lane,
    p: Palette,
    sx: number,
    footYFQ: number,
    pose: Pose,
    worldX: number,
  ): void {
    const ctx = this.ctx;
    const u = GRID_PX;
    const footY = lane.groundY - (footYFQ / FQ_ONE) * u;
    const phase = ((worldX / FQ_ONE) * 1.9) % 1;
    const swing = Math.sin(phase * Math.PI * 2);
    const px = (fq: number): number => (fq / FQ_ONE) * u;

    ctx.save();
    ctx.translate(sx, footY);
    ctx.lineCap = 'round';
    ctx.strokeStyle = p.ink;
    ctx.fillStyle = p.ink;

    if (pose === 'dead') {
      this.drawFairCorpse(p);
      ctx.restore();
      return;
    }

    const [body] = runnerMassShapes(pose, true);
    if (body && body.kind === 'roundrect') {
      ctx.beginPath();
      roundRect(
        ctx,
        px(body.r.x0),
        -px(body.r.y1),
        px(body.r.x1 - body.r.x0),
        px(body.r.y1 - body.r.y0),
        px(body.rad),
      );
      ctx.fill();
    }

    ctx.lineWidth = 0.12 * u;
    if (pose === 'slide') {
      // Trailing arm + speed dust, the shipped slide character.
      ctx.beginPath();
      ctx.moveTo(-0.3 * u, -0.3 * u);
      ctx.lineTo(-0.64 * u, -0.04 * u);
      ctx.stroke();
      ctx.globalAlpha *= 0.5;
      ctx.fillStyle = p.accent;
      ctx.fillRect(-1.35 * u, -0.1 * u, 0.5 * u, 3);
      ctx.fillRect(-1.0 * u, -0.28 * u, 0.35 * u, 3);
      ctx.globalAlpha = 1;
      ctx.fillStyle = p.bg;
      ctx.beginPath();
      ctx.arc(0.26 * u, -0.62 * u, 0.055 * u, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }

    if (pose === 'jump') {
      // Arms swept up past the capsule edges.
      ctx.beginPath();
      ctx.moveTo(0.24 * u, -1.24 * u);
      ctx.lineTo(0.52 * u, -1.5 * u);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-0.24 * u, -1.24 * u);
      ctx.lineTo(-0.5 * u, -1.32 * u);
      ctx.stroke();
    } else {
      // Stride: arms swing out of the capsule with the run phase.
      const armSwing = swing * 0.3 * u;
      ctx.beginPath();
      ctx.moveTo(0.2 * u, -1.2 * u);
      ctx.lineTo(0.36 * u + Math.abs(armSwing) * 0.6, -0.9 * u + armSwing * 0.3);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-0.2 * u, -1.2 * u);
      ctx.lineTo(-0.36 * u - Math.abs(armSwing) * 0.6, -0.9 * u - armSwing * 0.3);
      ctx.stroke();
    }

    const bob = pose === 'run' ? Math.abs(swing) * 2.2 : 0;
    ctx.fillStyle = p.bg;
    ctx.beginPath();
    ctx.arc(0.16 * u, -1.52 * u + bob * 0.5, 0.055 * u, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /**
   * The fair corpse: the live capsule, knocked onto its back. Caller has
   * already translated to the hitbox center at foot level and set the ink
   * tokens. Everything about the body comes from `runnerCorpse()` — the fall
   * angle and the lift that rests it on the ground included — so the corpse
   * cannot drift from the living body it is made of. It falls BACKWARD (pinned
   * in geometry.ts): the whole mass stays behind the kill box's leading edge,
   * leaving the death flash's boxes, their overlap fill, and the obstacle
   * itself uncovered by the body they explain.
   */
  private drawFairCorpse(p: Palette): void {
    const ctx = this.ctx;
    const u = GRID_PX;
    const px = (fq: number): number => (fq / FQ_ONE) * u;
    const corpse = runnerCorpse();
    const r = corpse.capsule.r;

    ctx.save();
    ctx.translate(0, -px(corpse.liftFQ));
    ctx.rotate(-corpse.lean); // canvas y points down: negate the world angle
    ctx.beginPath();
    roundRect(ctx, px(r.x0), -px(r.y1), px(r.x1 - r.x0), px(r.y1 - r.y0), px(corpse.capsule.rad));
    ctx.fill();
    // The face rides the body — same dot as alive, now staring at the sky.
    ctx.fillStyle = p.bg;
    ctx.beginPath();
    ctx.arc(0.16 * u, -1.52 * u, 0.055 * u, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Slack limbs, drawn upright — gravity does not rotate with the body.
    ctx.lineWidth = 0.12 * u;
    ctx.beginPath();
    ctx.moveTo(-0.02 * u, -0.2 * u);
    ctx.lineTo(0.24 * u, -0.42 * u);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-0.06 * u, -0.42 * u);
    ctx.lineTo(0.2 * u, -0.66 * u);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-1.5 * u, -0.62 * u);
    ctx.lineTo(-1.86 * u, -0.16 * u);
    ctx.stroke();

    // Impact scuff in the wedge the fallen body leaves over the ground.
    ctx.globalAlpha *= 0.5;
    ctx.fillStyle = p.inkSoft;
    ctx.fillRect(-1.94 * u, -0.06 * u, 0.34 * u, 4);
    ctx.fillRect(-1.5 * u, -0.16 * u, 0.24 * u, 4);
  }

  // --- effects & HUD ------------------------------------------------------

  /** Slow-mo is a player-lane beat (near miss), so the streaks live there too. */
  private drawSlowMoStreaks(): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = this.palette.ink;
    for (let i = 0; i < 6; i++) {
      const y = PLAYER_LANE.top + 18 + i * 24;
      ctx.fillRect(LOGICAL_W - 40 - i * 34, y, 120, 3);
    }
    ctx.restore();
  }

  /** Ink text on a bg chip (accent underline): raw accent text over the white
   *  overtake flash was near-invisible at the game's best moment. */
  private drawFloating(list: readonly FloatingText[]): void {
    const ctx = this.ctx;
    const p = this.palette;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const f of list) {
      const k = 1 - f.life / f.maxLife; // 0 → 1 over lifetime
      ctx.globalAlpha = f.life < f.maxLife * 0.3 ? f.life / (f.maxLife * 0.3) : 1;
      ctx.font = `800 ${f.size}px ${FONT_STACK}`;
      const y = f.sy - k * 46;
      const w = ctx.measureText(f.text).width;
      const padX = 14;
      const h = f.size + 18;
      ctx.fillStyle = p.bg;
      ctx.beginPath();
      roundRect(ctx, f.sx - w / 2 - padX, y - h / 2, w + padX * 2, h, h / 2);
      ctx.fill();
      ctx.fillStyle = f.color;
      ctx.fillRect(f.sx - w / 2, y + h / 2 - 4, w, 3);
      ctx.fillStyle = p.ink;
      ctx.fillText(f.text, f.sx, y);
    }
    ctx.restore();
  }

  private drawHud(scene: Scene): void {
    const ctx = this.ctx;
    const p = this.palette;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    ctx.fillStyle = p.ink;
    ctx.font = `800 76px ${FONT_STACK}`;
    ctx.fillText(String(scene.distanceM), LOGICAL_W / 2, 108);
    ctx.font = `600 20px ${FONT_STACK}`;
    ctx.fillStyle = p.inkSoft;
    ctx.fillText(scene.metersLabel, LOGICAL_W / 2, 136);

    if (scene.leadText !== null) {
      // Peak arm, after the overtake (§4bis.22): the same field, dressed as a
      // lead counter — 「领先 +{d} 米」 in the palette accent.
      ctx.fillStyle = p.accent;
      ctx.font = `800 30px ${FONT_STACK}`;
      ctx.fillText(scene.leadText, LOGICAL_W / 2, 178);
    } else if (scene.gapM !== null) {
      const ahead = scene.gapM >= 0;
      const sign = ahead ? '+' : '−';
      ctx.fillStyle = ahead ? p.good : p.bad;
      ctx.font = `800 30px ${FONT_STACK}`;
      ctx.fillText(`${sign}${Math.abs(scene.gapM)}${scene.metersLabel}`, LOGICAL_W / 2, 178);
    }
    ctx.restore();
  }
}

// --- helpers --------------------------------------------------------------

const EMPTY_MARKS: readonly number[] = [];

export function worldToScreenX(xFQ: number, camXFQ: number): number {
  return anchorPx + ((xFQ - camXFQ) / FQ_ONE) * GRID_PX;
}

export function poseOf(a: ActorSnapshot): Pose {
  if (a.dead) return 'dead';
  if (a.sliding) return 'slide';
  if (!a.grounded) return 'jump';
  return 'run';
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** #rrggbb → #rrggbb, `t` of the way from `a` to `b`. */
function mixHex(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  const k = clamp01(t);
  let out = '#';
  for (let i = 0; i < 3; i++) {
    const v = Math.round((ca[i] ?? 0) + ((cb[i] ?? 0) - (ca[i] ?? 0)) * k);
    out += Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
  }
  return out;
}

function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? `${h[0] ?? '0'}${h[0] ?? '0'}${h[1] ?? '0'}${h[1] ?? '0'}${h[2] ?? '0'}${h[2] ?? '0'}`
      : h;
  const n = Number.parseInt(full.slice(0, 6), 16);
  if (!Number.isFinite(n)) return [0, 0, 0];
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

/** Truncates with an ellipsis to fit `maxW` at the context's current font. */
function fitText(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  let s = text;
  while (s.length > 1 && ctx.measureText(`${s}…`).width > maxW) s = s.slice(0, -1);
  return `${s}…`;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/** Obstacles overlapping the visible window, cheaply (list is x-sorted). */
function* visible(obstacles: readonly Obstacle[], camX: number): Generator<Obstacle> {
  const leftFQ = camX - ((PLAYER_SCREEN_X + 80) / GRID_PX) * FQ_ONE;
  const rightFQ = camX + (((LOGICAL_W - PLAYER_SCREEN_X) + 80) / GRID_PX) * FQ_ONE;
  let lo = 0;
  let hi = obstacles.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const o = obstacles[mid];
    if (o && o.x + o.w < leftFQ) lo = mid + 1;
    else hi = mid;
  }
  for (let i = lo; i < obstacles.length; i++) {
    const o = obstacles[i];
    if (!o) break;
    if (o.x > rightFQ) break;
    yield o;
  }
}
